/** Tiny Web Audio bleeper: no assets, pure retro square/triangle blips. */
export class SoundFx {
  enabled = true;

  private ctx: AudioContext | null = null;

  private timers = new Set<number>();

  /**
   * Battery capture: the pitch rises with the combo step so the player hears the
   * multiplier climbing (x2 soft ping to x5 triumphant chime).
   */
  battery(step = 2): void {
    if (!this.ac()) return;
    const s = Math.max(2, Math.min(5, step));
    const root = 523 * 2 ** ((s - 2) / 4);
    const notes = s >= 5 ? [root, root * 1.25, root * 1.5, root * 2] : [root, root * 1.5];
    const dur = s >= 5 ? 0.13 : 0.09;
    const gain = s >= 5 ? 0.07 : 0.06;
    const gapMs = s >= 5 ? 55 : 50;
    notes.forEach((f, i) => this.schedule(i * gapMs, () => this.blip(f, dur, 'triangle', gain)));
  }

  /** Release the AudioContext so it does not leak across mounts (browsers cap concurrent contexts). */
  close(): void {
    // Cancel pending blips first; otherwise one firing after close would rebuild a fresh, leaked context.
    this.timers.forEach((id) => window.clearTimeout(id));
    this.timers.clear();
    if (!this.ctx) return;
    this.ctx.close().catch(() => undefined);
    this.ctx = null;
  }

  crash(): void {
    const ctx = this.ac();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(280, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.4);
    g.gain.setValueAtTime(0.09, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    osc.connect(g).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.45);
  }

  levelUp(): void {
    if (!this.ac()) return;
    [523, 659, 784, 1047].forEach((f, i) => this.schedule(i * 80, () => this.blip(f, 0.12, 'square', 0.05)));
  }

  /** Bright three-note flourish when a score milestone is crossed. */
  milestone(): void {
    if (!this.ac()) return;
    [880, 1175, 1568].forEach((f, i) => this.schedule(i * 70, () => this.blip(f, 0.14, 'square', 0.05)));
  }

  point(): void {
    this.blip(880, 0.06, 'triangle', 0.05);
  }

  thrust(): void {
    this.blip(220, 0.05, 'square', 0.025);
  }

  /** Browsers require a user gesture before audio can start. */
  unlock(): void {
    this.ac();
  }

  private ac(): AudioContext | null {
    if (!this.enabled) return null;
    if (!this.ctx) {
      const Ctor =
        window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      this.ctx = new Ctor();
    }
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => undefined);
    return this.ctx;
  }

  private blip(freq: number, dur: number, type: OscillatorType, gain = 0.06): void {
    const ctx = this.ac();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    g.gain.setValueAtTime(gain, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    osc.connect(g).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + dur);
  }

  /** Schedule a delayed blip, tracking the timer id so close() can cancel any still pending. */
  private schedule(delayMs: number, run: () => void): void {
    const id = window.setTimeout(() => {
      this.timers.delete(id);
      run();
    }, delayMs);
    this.timers.add(id);
  }
}
